import { Controller } from "@hotwired/stimulus";
import SiteTableController from "../admin/pos/site_table_controller";

const DOT_CLASSES = {
  pending: "bg-gray-300", ok: "bg-green-500", warn: "bg-amber-500", fail: "bg-red-500", unavailable: "bg-gray-500",
};
const DOT_TITLES = {
  pending: "Checking…", ok: "Healthy", warn: "Needs a look", fail: "Problems found", unavailable: "Couldn't check",
};

const blankStats = () => ({ total: 0, pending: 0, ok: 0, warn: 0, fail: 0, unavailable: 0 });

// The super admin Wash Health tenant tree and overview, rolled up from the site table's rows on each "updated".
export default class extends Controller {
  static targets = ["allItem", "tenantItem", "siteList", "siteItem", "heading", "overview", "locations", "content",
    "total", "tenantOnly", "overviewOnly", "attentionDelta",
    "unavailableCount", "camerasReading", "camerasTotal", "washesCount"];

  connect() {
    this.parts = new WeakMap();
    this.show(this.table.tenantValue, this.table.causeValue);
  }

  disconnect() {
    cancelAnimationFrame(this.frame);
  }

  get table() {
    return this.application.getControllerForElementAndIdentifier(this.element, "admin--pos--site-table");
  }

  // A tenant, All tenants, or a cause. The open tenant's link closes it.
  pick(event) {
    if (!SiteTableController.plainClick(event)) return;
    event.preventDefault();
    const { tenantId = "", cause = "" } = event.currentTarget.dataset;
    this.choose(tenantId && tenantId === this.table.tenantValue ? "" : tenantId, cause);
    this.contentTarget.scrollTo({ top: 0 });
  }

  pickSite(event) {
    if (!SiteTableController.plainClick(event)) return;
    event.preventDefault();
    const item = event.currentTarget;
    this.choose(item.dataset.tenantId);
    const row = document.getElementById(item.dataset.rowId);
    if (!row) return;
    // A status filter may be hiding it.
    if (row.hidden) {
      this.table.statusValue = "all";
      this.table.applyScope();
    }
    this.siteItemTargets.forEach((site) => site.setAttribute("aria-current", String(site === item)));
    row.scrollIntoView({ block: "center" });
    row.classList.add("bg-brand-50");
    setTimeout(() => row.classList.remove("bg-brand-50"), 1600);
  }

  choose(tenantId, cause = "") {
    this.table.tenantValue = tenantId;
    this.table.causeValue = cause;
    // A cause view has no status pills, so a leftover filter would hide its sites.
    if (cause) this.table.statusValue = "all";
    this.table.applyScope();
    this.show(tenantId, cause);
  }

  show(tenantId, cause = "") {
    const detail = Boolean(tenantId || cause);
    this.allItemTarget.setAttribute("aria-current", String(!tenantId));
    this.tenantItemTargets.forEach((item) => {
      item.setAttribute("aria-current", String(item.dataset.tenantId === tenantId));
    });
    this.siteListTargets.forEach((list) => list.classList.toggle("hidden", list.dataset.tenantId !== tenantId));
    this.headingTargets.forEach((heading) => {
      const matches = heading.dataset.tenantId === tenantId && (heading.dataset.cause || "") === cause;
      heading.classList.toggle("hidden", !matches);
    });
    this.overviewTarget.classList.toggle("hidden", detail);
    this.locationsTarget.classList.toggle("hidden", !detail);
    this.overviewOnlyTargets.forEach((element) => element.classList.toggle("hidden", detail));
    this.tenantOnlyTargets.forEach((element) => element.classList.toggle("hidden", !tenantId));
    if (!tenantId) this.siteItemTargets.forEach((site) => site.setAttribute("aria-current", "false"));
    this.render();
  }

  refresh() {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      this.render();
    });
  }

  render() {
    const openTenant = this.table.tenantValue;
    const byTenant = new Map();
    const statusByRow = new Map();
    const all = { ...blankStats(), causes: {}, camerasReading: 0, camerasTotal: 0, washes: 0 };
    this.table.rowTargets.forEach((row) => {
      const status = row.dataset.siteStatus;
      const { tenantId } = row.dataset;
      if (tenantId === openTenant) statusByRow.set(row.id, status);
      if (!byTenant.has(tenantId)) byTenant.set(tenantId, blankStats());
      const tenant = byTenant.get(tenantId);
      tenant.total += 1;
      tenant[status] = (tenant[status] || 0) + 1;
      all.total += 1;
      all[status] = (all[status] || 0) + 1;
      if (status !== "pending") {
        all.camerasReading += Number(row.dataset.sortCameras || 0);
        all.camerasTotal += Number(row.dataset.camerasTotal || 0);
        all.washes += Number(row.dataset.washes || 0);
        (row.dataset.causes || "").split(" ").filter(Boolean).forEach((cause) => {
          all.causes[cause] = (all.causes[cause] || 0) + 1;
        });
      }
    });

    // Only the open tenant's locations are on screen.
    this.siteItemTargets.forEach((item) => {
      if (item.dataset.tenantId === openTenant) this.paintDot(item, statusByRow.get(item.dataset.rowId) || "pending");
    });
    this.paintTenant(this.allItemTarget, all);
    this.tenantItemTargets.forEach((item) => this.paintTenant(item, byTenant.get(item.dataset.tenantId) || blankStats()));
    this.paintOverview(all);
  }

  static rollUp(stats) {
    if (stats.fail || stats.unavailable) return "fail";
    if (stats.warn) return "warn";
    if (stats.pending || !stats.total) return "pending";
    return "ok";
  }

  static attention(stats) {
    return stats.fail + stats.warn + stats.unavailable;
  }

  // "▲ 9 since yesterday"; blank without a baseline.
  static delta(current, baseline, label) {
    if (baseline === "" || baseline === undefined || Number.isNaN(Number(baseline))) return { text: "", tone: "" };
    const change = current - Number(baseline);
    if (change === 0) return { text: `No change ${label}`, tone: "text-gray-500" };
    const tone = change > 0 ? "text-red-600" : "text-green-700";
    return { text: `${change > 0 ? "▲" : "▼"} ${Math.abs(change)} ${label}`, tone };
  }

  partsOf(element) {
    if (!this.parts.has(element)) {
      this.parts.set(element, {
        dot: element.querySelector("[data-dot]"), count: element.querySelector("[data-count]"),
      });
    }
    return this.parts.get(element);
  }

  paintDot(element, status) {
    const { dot } = this.partsOf(element);
    if (!dot || dot.dataset.status === status) return;
    dot.dataset.status = status;
    dot.classList.remove(...Object.values(DOT_CLASSES));
    dot.classList.add(DOT_CLASSES[status] || DOT_CLASSES.pending);
    dot.title = DOT_TITLES[status] || "";
  }

  paintTenant(item, stats) {
    this.paintDot(item, this.constructor.rollUp(stats));
    const attention = this.constructor.attention(stats);
    const { count } = this.partsOf(item);
    count.textContent = attention ? attention.toLocaleString() : "";
    count.title = attention ? `${attention} need${attention === 1 ? "s" : ""} attention` : "";
  }

  // Numbers wait for every row; baselines come from the history.
  paintOverview(all) {
    const checked = !all.pending && all.total > 0;
    const paintDelta = (element, current) => {
      const delta = checked
        ? this.constructor.delta(current, element.dataset.baseline, element.dataset.baselineLabel)
        : { text: "", tone: "" };
      element.textContent = delta.text;
      element.classList.remove("text-red-600", "text-green-700", "text-gray-500");
      if (delta.tone) element.classList.add(delta.tone);
    };

    if (this.hasTotalTarget) this.totalTarget.textContent = all.total.toLocaleString();
    const tiles = {
      unavailableCount: all.unavailable, camerasReading: all.camerasReading, camerasTotal: all.camerasTotal,
      washesCount: all.washes,
    };
    Object.entries(tiles).forEach(([name, value]) => {
      this[`${name}Targets`].forEach((element) => { element.textContent = checked ? value.toLocaleString() : "…"; });
    });
    this.attentionDeltaTargets.forEach((element) => paintDelta(element, this.constructor.attention(all)));

    const counts = Object.values(all.causes);
    const max = counts.length ? Math.max(...counts) : 0;
    this.element.querySelectorAll("[data-cause-count]").forEach((element) => {
      const count = all.causes[element.dataset.causeCount] || 0;
      element.textContent = checked ? count.toLocaleString() : "…";
    });
    this.element.querySelectorAll("[data-cause-bar]").forEach((element) => {
      const count = all.causes[element.dataset.causeBar] || 0;
      element.style.width = checked && max ? `${(count / max) * 100}%` : "0%";
    });
    this.element.querySelectorAll("[data-cause-delta]").forEach((element) => {
      paintDelta(element, all.causes[element.dataset.causeDelta] || 0);
    });
  }
}
